import {
  Paper,
  Typography,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Button,
  Box,
  Link,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { processTime } from "fileParsing/wrangler/date";
import { suggestDate } from "fileParsing/wrangler/transform";
import { DateLanguages, DateParserArguments } from "fileParsing/wrangler/types";
import TinyTimeline from "./Visuals/TinyTimeline";

interface Props<T extends { arguments?: DateParserArguments }> {
  format?: T;
  samples: (string | number)[];
  onConfirm: (value: T) => void;
  onCancel: () => void;
}

enum Options {
  Automatic = 0,
  Lang = 1,
  Pattern = 2,
}

const TimestampArgumentModal: <T extends { arguments?: DateParserArguments }>(
  props: Props<T>,
) => React.ReactElement = ({ format, samples, onConfirm, onCancel }) => {
  const [selected, setSelected] = useState<{ option?: Options } & { arguments?: DateParserArguments }>({
    arguments: format?.arguments,
  });

  const [automatic, autoLang, description] = useMemo(() => {
    for (const sample of samples) {
      const fmt = suggestDate(sample);
      if (fmt) {
        const lang = {
          lang: ((langVal) => (!langVal || isNaN(Number(langVal)) ? langVal : DateLanguages[langVal]))(
            "lang" in fmt.argument ? fmt.argument.lang : undefined,
          ),
        };

        const text = "lang" in fmt.argument ? `Language (${lang.lang})` : "Unix-style Timestamp (Numeric)";

        if (selected.option === undefined && selected.arguments) {
          if ("format" in selected.arguments) {
            setSelected({ ...selected, option: Options.Pattern });
          } else {
            setSelected({
              ...selected,
              option: "lang" in selected.arguments ? Options.Lang : Options.Automatic,
            });
          }
        }

        return [fmt.argument, lang, text];
      }
    }
    return [undefined, undefined, "Not available"];
  }, [samples]);

  const formatDate = (date: Date | undefined) => {
    if (date) {
      const pad = (s: number) => String(s).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(
        date.getMinutes(),
      )}`;
    }
    return "";
  };

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "fit-content",
        padding: "30px 0 0 0",
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      <Box sx={{ padding: "0 30px 10px 30px" }}>
        <Typography variant="h4">Parse Timestamp</Typography>
        {!format || !format.arguments ? (
          <Alert variant="outlined" severity="warning">
            Format of timestamp not recognised.
            <br />
            Customize how timestamp is parsed.
          </Alert>
        ) : null}
        {samples.slice(0, 4).map((s) => (
          <div key={`date-${s}`}>
            {s} = {selected.arguments && formatDate(processTime(s, selected.arguments))}
          </div>
        ))}
        <FormControl>
          <RadioGroup value={selected.option ?? null}>
            <FormControlLabel
              value={Options.Automatic}
              control={<Radio />}
              onChange={() => setSelected({ option: Options.Automatic, arguments: automatic })}
              label={`Option 0 - Automatic: ${description}`}
              disabled={automatic === undefined}
            />
            <FormControlLabel
              value={Options.Lang}
              control={<Radio />}
              label="Option 1 - Choose Language:"
              disabled={autoLang === undefined}
            />
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={selected.arguments && "lang" in selected.arguments ? selected.arguments.lang : null}
                onChange={(ev) =>
                  setSelected({
                    option: Options.Lang,
                    arguments: { lang: ev.target.value as DateLanguages },
                  })
                }
              >
                {Object.keys(DateLanguages)
                  .filter((lang) => isNaN(Number(lang)))
                  .map((lang) => (
                    <MenuItem value={lang} key={lang}>
                      {lang}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControlLabel
              value={Options.Pattern}
              control={<Radio />}
              label="Option 2 - Input Custom Parser Pattern:"
              onChange={() =>
                setSelected({
                  option: Options.Pattern,
                  arguments: { pattern: "" },
                })
              }
            />
            <FormControl fullWidth>
              <TextField
                label="Custom Format"
                variant="standard"
                value={selected?.arguments && "format" in selected.arguments ? (selected?.arguments?.format ?? "") : ""}
                onChange={(ev) =>
                  setSelected({
                    option: Options.Pattern,
                    arguments: { pattern: ev.target.value as string },
                  })
                }
              />
              <FormHelperText>
                see:{" "}
                <Link href="https://github.com/d3/d3-time-format#locale_format" rel="noreferrer" target="_blank">
                  https://github.com/d3/d3-time-format#locale_format
                </Link>
              </FormHelperText>
            </FormControl>
          </RadioGroup>
        </FormControl>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="text"
            onClick={() => {
              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selected}
            onClick={() => {
              if (format !== undefined) {
                onConfirm({ ...format, arguments: selected?.arguments });
              }
            }}
          >
            Confirm
          </Button>
        </div>
      </Box>
      <TinyTimeline
        data={
          selected.arguments
            ? samples
                // deterministic approach to select 1000 items from the array
                .filter((_, i, arr) => i % Math.ceil(arr.length / 1000) === 0)
                .map((date) => ({
                  date: selected.arguments && processTime(date, selected.arguments),
                }))
            : []
        }
        size={{ width: "100%", height: 100 }}
      />
    </Paper>
  );
};

export default TimestampArgumentModal;
